import { getDeviceType } from '@/utils/device';
import { LAYOUT_DETAILS } from './config';
import { isCompatibleLayout, getLayoutCookie, setLayoutCookie } from './utils';
import { GaEventTypes, Layout } from './types';
import { debounce } from '@/utils/timing';

export class MediaLayoutSwitcher {
    layout: Layout;
    listCounter: number;
    debouncedResize: ReturnType<typeof debounce>;
    readonly ga: GaEventTypes = {
        event: 'GA4 - Generic Event',
        name: 'click_layout_change_icon',
    };
    readonly mediaListLayoutChangedEventName: 'mediaListLayoutChanged' = 'mediaListLayoutChanged';

    constructor({ layout, listCounter }: { layout: Layout; listCounter: number }) {
        this.layout = layout;
        this.listCounter = listCounter;
        this.init();
    }

    init() {
        const cachedLayout = getLayoutCookie() as Layout;
        const layout = this.getValidLayoutByDeviceType(cachedLayout);

        this.setLayout(layout);
        this.debouncedResize = debounce(this.handleResize.bind(this), 300);
        window.addEventListener('resize', this.debouncedResize);
    }

    incrementListCounter() {
        this.listCounter++;
    }

    getLayout(): Layout {
        return this.layout;
    }

    getListCounter(): number {
        return this.listCounter;
    }

    getValidLayoutByDeviceType(layout: Layout) {
        const deviceType = getDeviceType();

        if (isCompatibleLayout(layout, deviceType)) {
            return layout;
        }

        return LAYOUT_DETAILS[deviceType]?.default;
    }

    setLayout(layout: Layout): void {
        this.layout = layout;
    }

    handleResize() {
        const cachedLayout = getLayoutCookie() as Layout;
        const layout = this.getValidLayoutByDeviceType(cachedLayout);

        this.setLayout(layout);
    }

    getMediaListLayoutChangedEventName(): string {
        return this.mediaListLayoutChangedEventName;
    }

    onLayoutChange(newLayout: Layout): void {
        const currentLayout = getLayoutCookie() as Layout;
        const layout = this.getValidLayoutByDeviceType(newLayout);

        this.setLayout(layout);
        setLayoutCookie(layout);

        this.sendGaClickEvent(currentLayout, newLayout);

        window.dispatchEvent(new Event(this.getMediaListLayoutChangedEventName()));
    }

    getGaEventFromTo(prevLayout: Layout, nextLayout: Layout): string {
        return `${prevLayout}_${nextLayout}`;
    }

    sendGaClickEvent(prevLayout: Layout, nextLayout: Layout): void {
        if (window.dataLayer) {
            window.dataLayer.push({
                event: this.ga.event,
                event_name: this.ga.name,
                from_to: this.getGaEventFromTo(prevLayout, nextLayout),
            });
        } else {
            console.error('window dataLayer is not available');
        }
    }

    destroy(): void {
        if (this.debouncedResize) {
            window.removeEventListener('resize', this.debouncedResize);
        }
    }
}
